// Syntax-colored JSON viewer for the API playground.
//
// Color vocabulary (reuses app status tokens):
//   • keys        → text-primary (gold)
//   • strings     → text-statusGreen
//   • numbers     → text-statusBlue
//   • boolean t   → text-statusGreen
//   • boolean f   → text-statusRed
//   • null        → text-textMuted
//   • punctuation → text-textSecondary
//
// Objects + arrays beyond `defaultCollapseDepth` start collapsed so a 100-row
// response doesn't crater the viewport on first render. Click the ▶/▼ caret to
// toggle.

import React, {useState} from 'react'
import clsx from 'clsx'

interface JsonViewerProps {
  value: unknown
  /** Depth at which to start collapsed. Top level is depth 0. */
  defaultCollapseDepth?: number
}

const INDENT = 'pl-md'

export const JsonViewer: React.FC<JsonViewerProps> = ({value, defaultCollapseDepth = 2}) => (
  <pre className="overflow-auto whitespace-pre rounded-lg bg-surface p-lg font-mono text-sm leading-relaxed">
    <JsonNode value={value} depth={0} defaultCollapseDepth={defaultCollapseDepth} isLast />
  </pre>
)

interface NodeProps {
  value: unknown
  depth: number
  defaultCollapseDepth: number
  /** When false, render a trailing comma after the value. */
  isLast: boolean
  /** When provided, render `"<keyName>": ` before the value. */
  keyName?: string
}

const Punct: React.FC<{children: React.ReactNode}> = ({children}) => (
  <span className="text-textSecondary">{children}</span>
)

const JsonNode: React.FC<NodeProps> = ({value, depth, defaultCollapseDepth, isLast, keyName}) => {
  const keyPart = keyName != null && (
    <>
      <span className="text-primary">"{keyName}"</span>
      <Punct>: </Punct>
    </>
  )
  const trailing = !isLast ? <Punct>,</Punct> : null

  if (value === null) {
    return (
      <span>
        {keyPart}
        <span className="text-textMuted">null</span>
        {trailing}
      </span>
    )
  }
  if (typeof value === 'string') {
    return (
      <span>
        {keyPart}
        <span className="text-statusGreen">"{escapeString(value)}"</span>
        {trailing}
      </span>
    )
  }
  if (typeof value === 'number') {
    return (
      <span>
        {keyPart}
        <span className="text-statusBlue">{String(value)}</span>
        {trailing}
      </span>
    )
  }
  if (typeof value === 'boolean') {
    return (
      <span>
        {keyPart}
        <span className={value ? 'text-statusGreen' : 'text-statusRed'}>{String(value)}</span>
        {trailing}
      </span>
    )
  }
  if (Array.isArray(value)) {
    return (
      <CollapsibleContainer
        keyPart={keyPart}
        open={depth < defaultCollapseDepth}
        openBracket="["
        closeBracket="]"
        summary={`Array(${value.length})`}
        trailing={trailing}
        emptyBracket={value.length === 0}
        depth={depth}
      >
        {value.map((v, i) => (
          <div key={i} className={INDENT}>
            <JsonNode
              value={v}
              depth={depth + 1}
              defaultCollapseDepth={defaultCollapseDepth}
              isLast={i === value.length - 1}
            />
          </div>
        ))}
      </CollapsibleContainer>
    )
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
    return (
      <CollapsibleContainer
        keyPart={keyPart}
        open={depth < defaultCollapseDepth}
        openBracket="{"
        closeBracket="}"
        summary={`Object(${entries.length})`}
        trailing={trailing}
        emptyBracket={entries.length === 0}
        depth={depth}
      >
        {entries.map(([k, v], i) => (
          <div key={k} className={INDENT}>
            <JsonNode
              value={v}
              depth={depth + 1}
              defaultCollapseDepth={defaultCollapseDepth}
              isLast={i === entries.length - 1}
              keyName={k}
            />
          </div>
        ))}
      </CollapsibleContainer>
    )
  }
  return <span>{String(value)}</span>
}

interface CollapsibleContainerProps {
  keyPart: React.ReactNode
  open: boolean
  openBracket: string
  closeBracket: string
  summary: string
  trailing: React.ReactNode
  emptyBracket: boolean
  depth: number
  children: React.ReactNode
}

const CollapsibleContainer: React.FC<CollapsibleContainerProps> = ({
  keyPart,
  open,
  openBracket,
  closeBracket,
  summary,
  trailing,
  emptyBracket,
  children,
}) => {
  const [expanded, setExpanded] = useState(open)
  if (emptyBracket) {
    return (
      <span>
        {keyPart}
        <Punct>
          {openBracket}
          {closeBracket}
        </Punct>
        {trailing}
      </span>
    )
  }
  return (
    <span>
      {keyPart}
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className={clsx(
          'mr-xs cursor-pointer text-textMuted hover:text-textPrimary',
          'inline-block w-3 text-center align-middle',
        )}
        aria-label={expanded ? 'Collapse' : 'Expand'}
      >
        {expanded ? '▼' : '▶'}
      </button>
      <Punct>{openBracket}</Punct>
      {expanded ? (
        <>
          <div>{children}</div>
          <Punct>{closeBracket}</Punct>
        </>
      ) : (
        <>
          <span className="text-textMuted"> {summary} </span>
          <Punct>{closeBracket}</Punct>
        </>
      )}
      {trailing}
    </span>
  )
}

function escapeString(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n')
}
